import { forwardRef, useState, useCallback, useEffect, FocusEvent } from 'react';
import { getExampleNumber, parsePhoneNumber, type CountryCode } from 'libphonenumber-js/max';
import { AsYouType, getCountries, getCountryCallingCode } from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import { ConfigService, optionalProp } from '../core';
import type { IconName } from '../Icons/Icons';
import { Theme, MLPVersion } from '../types/enums';
import { type Data } from '../DropDown/types/propsType';
import { InputTypes, InputValidations, InputModes } from '../Input/Input';
import { FAIcon } from '../Icons/Icons';
import { useMasterData } from '../MasterData/MasterDataProvider';
import { NextgenImage } from '../NextgenImage/NextgenImage';
import PhoneNumberMLP1 from './mlp1/PhoneNumberMLP1';
import PhoneNumberMLP2 from './mlp2/PhoneNumberMLP2';
import CountrySelector from './CountrySelector/CountrySelector';
import { type PhoneNumberProps } from './types/propsType';
import { type Country } from './types/countryInterface';
import { type TriggerChangeEventProptype, type InputChangeArgs } from './types/inputChangeHanderInterface';
import styles from './PhoneNumber.module.scss';

const { DEFAULT, INVALID, VALID } = InputValidations;

const compareByName = (a: { name?: string; countryName?: string }, b: { name?: string; countryName?: string }) => {
  const firstName = (a.name || a.countryName) as string;
  const secondName = (b.name || b.countryName) as string;
  return firstName.localeCompare(secondName);
};

const checkMLPVersion = (mlpVersion: MLPVersion, toCompareMLPVersion: MLPVersion) => mlpVersion === toCompareMLPVersion;

const PHONE_NUMBER_LENGTH_OVERRIDES: Partial<Record<CountryCode, number>> = {
  // Workaround: override Phonelib’s incorrect default for Indonesian phone number length by hardcoding the correct length.
  ID: 11,
};

export const getPhoneNumberLengthByCountry = (countryCode: CountryCode): number | undefined => {
  if (typeof countryCode !== 'string') {
    return undefined;
  }
  const cc = countryCode.toUpperCase() as CountryCode;
  // check overrides first
  if (PHONE_NUMBER_LENGTH_OVERRIDES[cc] !== undefined) {
    return PHONE_NUMBER_LENGTH_OVERRIDES[cc];
  }
  // fall back to the example data
  return getExampleNumber(cc, examples)?.nationalNumber?.length;
};

const getSelectedCountry = ({ dropdownValue, data }: { dropdownValue?: string | number | Data; data?: Data[] }) =>
  dropdownValue
    ? data?.filter((item) => {
        if (typeof dropdownValue === 'string') {
          return (
            item.name?.toString().replaceAll(/[()']+/g, '') === dropdownValue?.toString().replaceAll(/[()']+/g, '')
          );
        } else if (typeof dropdownValue === 'object') {
          return (
            item.name?.toString().replaceAll(/[()']+/g, '') ===
            dropdownValue?.name?.toString().replaceAll(/[()']+/g, '')
          );
        }
      })?.[0]
    : data?.[0];

const getAllowedLength = (selectedCountryData: any, autoLeadingZero: boolean): number => {
  const baseLength = Number(selectedCountryData?.phoneNumberLength) || 0;
  return autoLeadingZero ? baseLength + 1 : baseLength;
};

const extractNumeric = (value: string | undefined): string => value?.match(/\d+/g)?.join('') || '';

const VALID_PHONE_CHARS_REGEX = /[^0-9+\-\s()]/g;

export const sanitizePhone = (raw?: string): string => (raw ?? '').replace(VALID_PHONE_CHARS_REGEX, '');

export const applyAutoLeadingZero = (
  resolvedValue: string | undefined,
  autoLeadingZero: boolean,
): string | undefined => {
  if (resolvedValue === undefined) {
    return resolvedValue;
  }
  if (resolvedValue === '') {
    return '';
  } // Do not prepend zero if the resolved value is empty // CHANGED
  return autoLeadingZero && !resolvedValue.startsWith('0') ? `0${resolvedValue}` : resolvedValue;
};

const getCountryFromDialCode = (dropdownValue: string | number | Data | undefined): CountryCode | undefined => {
  const value = dropdownValue && (typeof dropdownValue === 'object' ? dropdownValue.desc : dropdownValue)?.toString();
  if (!value) {
    return;
  }

  const dialCode = extractNumeric(value);

  return getCountries().find((country) => getCountryCallingCode(country) === dialCode);
};

const formatPhoneNumber = (
  value: string | undefined,
  dropdownValue: string | number | Data | undefined,
  autoLeadingZero: boolean,
  enableFormatting: boolean,
): string | undefined => {
  const resolvedValue = applyAutoLeadingZero(value, autoLeadingZero);
  if (resolvedValue === undefined) {
    return;
  }
  const countryCode = getCountryFromDialCode(dropdownValue);
  if (!enableFormatting || !countryCode) {
    return resolvedValue;
  }

  const rawNumericValue = extractNumeric(resolvedValue);
  const addZero = !resolvedValue.startsWith('0');
  const normalizedValue = addZero ? `0${rawNumericValue}` : rawNumericValue;
  const formattedValue = new AsYouType(countryCode).input(normalizedValue);
  return addZero ? formattedValue.slice(1) : formattedValue;
};

// Determine the resolved value based on input type and allowed length
export const getResolvedValue = (
  value: string,
  allowedLength: number,
  isCountrySelector: boolean,
  isMLP1: boolean,
  isTextInput: boolean,
  numericLength: number,
): string | undefined => {
  if (isCountrySelector || isMLP1) {
    return value.substring(0, allowedLength);
  }
  if (isTextInput) {
    return numericLength <= allowedLength ? value : undefined;
  }
  return allowedLength ? value.substring(0, allowedLength) : value;
};

const handleOnInputChange = ({
  isCountrySelector,
  mlpVersion,
  restProps,
  validatePhoneNumber,
  updateCurrentStatus,
  value: rawValue,
  data,
  onInputChange,
  autoLeadingZero,
  isAutoValidate,
  showErrorOnBlurOnly,
  onChange,
  changeEvent,
}: InputChangeArgs & { isAutoValidate: boolean; showErrorOnBlurOnly?: boolean }) => {
  const value = sanitizePhone(rawValue);
  const { dropdownValue, enableFormatting } = restProps;
  const numericPhoneNumber = extractNumeric(value);

  const triggerChangeEvent = ({ value, status }: TriggerChangeEventProptype) => {
    if (changeEvent && onChange) {
      const updatedEvent = /^\d+$/.test(changeEvent.target.value)
        ? { ...changeEvent, target: { ...changeEvent.target, value: value } }
        : changeEvent;
      onChange(updatedEvent);
    }
    onInputChange?.(value, status);
  };

  const returnWithDefault = (val: string) => {
    triggerChangeEvent({ value: val, status: DEFAULT });
    updateCurrentStatus(DEFAULT);
  };

  const getNumericWithLeadingZero = (): string => {
    if (numericPhoneNumber === '') {
      return '';
    }
    if (autoLeadingZero && !numericPhoneNumber.startsWith('0')) {
      return `0${numericPhoneNumber}`;
    }
    return numericPhoneNumber;
  };

  if (!dropdownValue || !data) {
    returnWithDefault(getNumericWithLeadingZero());
    return;
  }

  const selectedCountryData = getSelectedCountry({ dropdownValue, data });
  const allowedLength = getAllowedLength(selectedCountryData, autoLeadingZero);

  if (numericPhoneNumber === '') {
    if (isAutoValidate) {
      const currentStatus = validatePhoneNumber(dropdownValue as Data, '');
      triggerChangeEvent({ value: '', status: currentStatus });
      updateCurrentStatus(currentStatus);
    } else {
      returnWithDefault('');
    }
    return;
  }

  const numericWithLeadingZero = getNumericWithLeadingZero();
  const trimmedNumeric = numericWithLeadingZero.slice(0, allowedLength);
  const isMLP1 = checkMLPVersion(mlpVersion, MLPVersion.ONE);
  const isTextInput = isNaN(Number(value));

  let valueResolved = getResolvedValue(
    value || '',
    allowedLength,
    isCountrySelector,
    isMLP1,
    isTextInput,
    numericPhoneNumber.length,
  );

  valueResolved = formatPhoneNumber(valueResolved, dropdownValue, autoLeadingZero, enableFormatting);

  if (valueResolved === undefined) {
    return;
  }

  if (isAutoValidate && !showErrorOnBlurOnly) {
    const currentStatus = validatePhoneNumber(dropdownValue as Data, trimmedNumeric);
    triggerChangeEvent({ value: valueResolved, status: currentStatus });
    updateCurrentStatus(currentStatus);
  } else {
    triggerChangeEvent({ value: valueResolved, status: DEFAULT });
  }
};

const getDropdownData = (data: Data[] | undefined, countryData: Country[], mlpVersion: MLPVersion) => {
  if (data) {
    data.forEach((item) => {
      item.phoneNumberLength = getPhoneNumberLengthByCountry(item.id as CountryCode);
    });
    const sortedCountriesData = data.sort(compareByName);
    return sortedCountriesData;
  }

  if (countryData) {
    const sortedCountryData = countryData.sort((a, b) => a.countryName.localeCompare(b.countryName)); // Sorting the array alphabetically by countryName
    const configs = ConfigService.getConfigs();
    if (checkMLPVersion(mlpVersion, MLPVersion.ONE)) {
      return sortedCountryData.map((item) => ({
        id: item.pk,
        name: item.countryName,
        desc: `+${item.phoneCode}`,
        phoneNumberLength: getPhoneNumberLengthByCountry(item.pk as CountryCode),
        flagIcon: (
          <div>
            <NextgenImage src={`${configs.NEXT_ASSETS_BASE_PATH}/icons/flags/${item.icon}`} alt="flag icon" />
          </div>
        ),
      }));
    }

    return sortedCountryData.map((item) => ({
      id: item.pk,
      name: `(+${item.phoneCode})`,
      desc: item.countryName,
      phoneNumberLength: getPhoneNumberLengthByCountry(item.pk as CountryCode),
    }));
  }
  return [];
};

const inputValidationStatus = (status?: InputValidations) => {
  if (status) {
    if (status === VALID) {
      return styles['valid'];
    } else if (status === INVALID) {
      return styles['invalid'];
    }
  }

  return '';
};

const disabledStateClass = (dropdownDisabled: boolean, inputDisabled: boolean) => {
  if (dropdownDisabled && inputDisabled) {
    return styles['disabled'];
  } else if (dropdownDisabled) {
    return styles['disabled-dropdown'];
  } else if (inputDisabled) {
    return styles['disabled-input'];
  }
  return '';
};

const calculateCurrentIcon = function (status?: InputValidations) {
  if (status) {
    if (status === VALID) {
      return { iconName: 'circle-check' as IconName };
    } else if (status === INVALID) {
      return { iconName: 'square-exclamation' as IconName };
    }
  }

  return null;
};

const getChild = (
  isCountrySelector: boolean,
  phoneCountryData: Data[],
  dropdownDisabled: boolean,
  mlpVersion: MLPVersion,
  restProps: any,
  commonMLPProps: any,
) => {
  let child = <></>;
  const { autoLeadingZero, ...restArgs } = commonMLPProps;
  if (isCountrySelector) {
    child = (
      <CountrySelector
        data={phoneCountryData}
        dropdownDisabled={dropdownDisabled}
        status={restProps?.status}
        {...restProps}
        onChange={commonMLPProps.onChange}
      />
    );
  } else if (checkMLPVersion(mlpVersion, MLPVersion.ONE)) {
    child = <PhoneNumberMLP1 {...restArgs} />;
  } else if (checkMLPVersion(mlpVersion, MLPVersion.TWO)) {
    child = <PhoneNumberMLP2 {...restArgs} autoLeadingZero={autoLeadingZero} />;
  }
  return child;
};

export const phoneNumberValidation =
  (
    restProps: PhoneNumberProps,
    phoneCountryData: Data[],
    isPhoneNumberValid: (isValid: boolean) => void,
    autoLeadingZero: boolean,
  ) =>
  (selectedCountry: Data | string, inputNumber: string): InputValidations => {
    const configLabel = restProps.configData?.label ?? 'name';
    const requiredFallback = restProps.required ? INVALID : DEFAULT;

    const resolveCountry = (): Data | string | undefined => {
      if (typeof selectedCountry !== 'string') {
        return selectedCountry;
      }

      const match = phoneCountryData.find((pcd) => pcd[configLabel] === selectedCountry);
      return match || selectedCountry;
    };

    const currentCountry = resolveCountry();
    const isObjectCountry = currentCountry && typeof currentCountry === 'object';

    if (!isObjectCountry) {
      isPhoneNumberValid(false);
      return requiredFallback;
    }

    const numericInput = inputNumber.replace(/\D+/g, '');

    if (numericInput.length === 0) {
      isPhoneNumberValid(false);
      return requiredFallback;
    }

    const baseLength = Number(currentCountry.phoneNumberLength) || 0;
    const allowedLength = autoLeadingZero ? baseLength + 1 : baseLength;

    if (numericInput.length !== allowedLength) {
      isPhoneNumberValid(false);
      return INVALID;
    }
    const cleanedInputNumber = inputNumber?.startsWith('0') ? inputNumber.slice(1) : inputNumber;
    try {
      const parsed = parsePhoneNumber(cleanedInputNumber, (currentCountry.id as string).toUpperCase() as CountryCode);
      const isValid = parsed.isValid();

      isPhoneNumberValid(isValid);
      return isValid ? VALID : INVALID;
    } catch {
      isPhoneNumberValid(false);
      return requiredFallback;
    }
  };

/**
 *
 *  by default : disabledFilter, dropdownDisabled, inputDisabled is false;
 *  name is required prop
 */

const PhoneNumber = forwardRef<HTMLDivElement, PhoneNumberProps>(
  (
    {
      className,
      dropdownDisabled = false,
      data,
      inputLabel = '',
      inputDisabled = false,
      message,
      status,
      forwardedRef,
      isCountrySelector = false,
      mlpVersion = isCountrySelector ? MLPVersion.ONE : MLPVersion.TWO,
      filterDropdownValues,
      onInputChange,
      isAutoValidate = false,
      displayFlagIcon = false,
      autoLeadingZero = false,
      isPhoneNumberValid = () => null,
      inputWithDropdown = false,
      showErrorOnBlurOnly = false,
      enableFormatting = false,
      onChange,
      ...restProps
    }: PhoneNumberProps,
    ref,
  ): JSX.Element => {
    const [currentStatus, setCurrentStatus] = useState<InputValidations | undefined>(status);
    const countryData = useMasterData('MasterCountryList') as Country[];
    const resolvedCountryData = getDropdownData(data, countryData, mlpVersion);
    const phoneCountryData = filterDropdownValues ? filterDropdownValues(resolvedCountryData) : resolvedCountryData;
    const [isElementFocused, setIsElementFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const currentIcon = calculateCurrentIcon(currentStatus);
    const [searchFilterValue, setSearchFilterValue] = useState('');
    const [isTouched, setIsTouched] = useState(false);

    const { dropdownValue, inputValue } = restProps;
    const updateCurrentStatus = (value: InputValidations) => {
      if (isAutoValidate && status) {
        setCurrentStatus(status);
      } else {
        setCurrentStatus(value);
      }
    };
    const validatePhoneNumberFn = phoneNumberValidation(
      restProps,
      phoneCountryData,
      isPhoneNumberValid,
      autoLeadingZero,
    );
    const validatePhoneNumber = useCallback(
      (selectedCountry: Data, inputNumber: string) => validatePhoneNumberFn(selectedCountry, inputNumber),
      [restProps.dropdownValue, restProps.inputValue],
    );

    const shouldValidateInput = (
      isAutoValidate: boolean,
      dropdownValue: unknown,
      updatedValue: string,
      isTouched: boolean,
      showErrorOnBlurOnly: boolean,
    ): boolean => !showErrorOnBlurOnly && isAutoValidate && !!dropdownValue && (updatedValue !== '' || isTouched);

    const handleValidatedInput = (
      dropdownValue: Data,
      updatedValue: string,
      setStatus: (status: InputValidations) => void,
      onChangeCallback?: (value: string, status: InputValidations) => void,
    ): void => {
      const newStatus = validatePhoneNumber(dropdownValue, updatedValue || '');
      setStatus(newStatus as InputValidations);
      onChangeCallback?.(updatedValue, newStatus);
    };

    // On mount or input change:
    // - If inputValue exists, apply autoLeadingZero and validate immediately
    // - If inputValue is empty, validate only after user has interacted
    // - Ensures required validation does not trigger before user action
    useEffect(() => {
      const updatedValue = formatPhoneNumber(inputValue, dropdownValue, autoLeadingZero, enableFormatting) ?? '';

      if (shouldValidateInput(isAutoValidate, dropdownValue, updatedValue, isTouched, showErrorOnBlurOnly)) {
        handleValidatedInput(dropdownValue as Data, updatedValue, updateCurrentStatus, onInputChange);
      } else if (isAutoValidate) {
        updateCurrentStatus(DEFAULT);
      } else if (status) {
        updateCurrentStatus(status);
      }
    }, [dropdownValue, inputValue, status, isAutoValidate, isCountrySelector, isTouched]);

    const handleOnBlur = (e: FocusEvent<HTMLInputElement, Element>) => {
      restProps?.onBlur?.(e);
      if (showErrorOnBlurOnly && isAutoValidate) {
        const updatedValue = formatPhoneNumber(inputValue, dropdownValue, autoLeadingZero, enableFormatting) ?? '';
        handleValidatedInput(dropdownValue as Data, updatedValue, updateCurrentStatus, onInputChange);
      }
    };
    const commonMLPProps = {
      phoneCountryData,
      isElementFocused,
      setIsElementFocused,
      isHovered,
      inputLabel,
      setIsHovered,
      currentIcon,
      dropdownDisabled,
      inputDisabled,
      onChange: onChange,
      onInputChange: (
        value: string | undefined,
        _status: string | undefined,
        changeEvent: React.ChangeEvent<HTMLInputElement>,
      ) => {
        if (!isTouched) {
          setIsTouched(true);
        }
        handleOnInputChange({
          isCountrySelector,
          mlpVersion,
          restProps,
          validatePhoneNumber,
          updateCurrentStatus,
          ...{ ...optionalProp('value', value) },
          ...{ ...optionalProp('onInputChange', onInputChange) },
          ...{ ...optionalProp('changeEvent', changeEvent) },
          data: resolvedCountryData,
          autoLeadingZero,
          isAutoValidate,
          showErrorOnBlurOnly,
        });
      },
      status: currentStatus,
      searchFilterValue: searchFilterValue,
      setSearchFilterValue: setSearchFilterValue,
      displayFlagIcon: displayFlagIcon,
      ...restProps,
      inputValue: formatPhoneNumber(inputValue, dropdownValue, autoLeadingZero, enableFormatting),
      value: formatPhoneNumber(inputValue, dropdownValue, autoLeadingZero, enableFormatting),
      inputWithDropdown: inputWithDropdown,
      onBlur: handleOnBlur,
      ...(enableFormatting && {
        inputMode: InputModes.NUMERIC,
        type: InputTypes.TEXT,
      }),
      autoLeadingZero: autoLeadingZero,
    };

    return (
      <div
        className={`${styles['phone-number-wrapper']} ${disabledStateClass(
          dropdownDisabled,
          inputDisabled,
        )} ${inputValidationStatus(currentStatus)} ${className} ${styles[`mlp-${mlpVersion}`]}`}
        data-testid="phone-container"
        ref={ref ?? forwardedRef}
      >
        {getChild(isCountrySelector, phoneCountryData, dropdownDisabled, mlpVersion, restProps, commonMLPProps)}
        {message ? (
          <span
            role="presentation"
            aria-label="message"
            aria-details={`${inputLabel} phone number message`}
            className={`${styles['phone-number-message-container']}`}
          >
            {currentIcon ? (
              <FAIcon {...currentIcon} theme={Theme.BLACK} className={styles['phone-number-message-icon']} />
            ) : (
              ''
            )}
            {message && (
              <span data-testid="phone-number-message" className={styles['phone-number-message']}>
                {message}
              </span>
            )}
          </span>
        ) : (
          ''
        )}
      </div>
    );
  },
);
PhoneNumber.displayName = 'PhoneNumber';
export { InputTypes, MLPVersion };
export default PhoneNumber;
